/**
 * Footer fixe en bas de page — teal primary (cohérent avec la navbar).
 *
 * Rendu par `ProtectedLayout` seulement : le lien « Guide d'utilisation »
 * (page `/guide`) n'existe donc que pour un utilisateur connecté, et le
 * serveur revérifie la session (ADR 0046).
 */
import { BookOpen } from "lucide-react";
import { Link } from "react-router-dom";

import { APP_LOGO_TEXT, APP_SUBTITLE } from "@/config/navigation";
import { UpdateNotice } from "@/components/layout/UpdateNotice";
import { useAuthStore } from "@/features/auth/store";
import { useIsUpdateAvailable } from "@/lib/app-update";
import { useIsUiTemplateE } from "@/lib/hooks/use-ui-template";
import { contentMaxWidthClass } from "@/lib/content-width";
import { cn } from "@/lib/utils";

export function Footer() {
  const me = useAuthStore((s) => s.me);
  const updateAvailable = useIsUpdateAvailable();
  const uiE = useIsUiTemplateE();
  return (
    <footer
      id="app-footer"
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-primary bg-primary text-primary-foreground shadow-[0_-2px_8px_rgba(0,0,0,0.06)]"
    >
      <div
        className={cn(
          // Trois zones : identité à gauche, mise à jour AU CENTRE, liens à droite.
          // Les deux côtés ont la même largeur (1fr), donc le centre est exact.
          "mx-auto grid h-10 w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-4 text-xs lg:px-6",
          contentMaxWidthClass(me),
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 font-semibold">{APP_LOGO_TEXT}</span>
          <span
            className={cn(
              "hidden truncate text-primary-foreground/70",
              // La mention de mise à jour prend le centre : le sous-titre ne
              // s'affiche alors que sur les grands écrans, sans chevauchement.
              updateAvailable ? "xl:inline" : "sm:inline",
            )}
          >
            — {APP_SUBTITLE}
          </span>
          {/* Présentation E : mention de l'éditeur, sans logo. */}
          {uiE && (
            <span
              className={cn(
                "hidden shrink-0 text-primary-foreground/80",
                updateAvailable ? "2xl:inline" : "lg:inline",
              )}
            >
              · Un produit GOLDENTECH SARL
            </span>
          )}
        </div>
        <div className="flex justify-center">
          <UpdateNotice />
        </div>
        <div className="flex min-w-0 items-center justify-end gap-3">
          <Link
            to="/guide"
            title="Ouvrir le guide d'utilisation"
            className="inline-flex shrink-0 items-center gap-1 font-medium underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
          >
            <BookOpen className="h-3.5 w-3.5" aria-hidden />
            Guide d'utilisation
          </Link>
          <span
            className={cn(
              "hidden shrink-0 text-primary-foreground/80",
              updateAvailable ? "xl:inline" : "sm:inline",
            )}
          >
            © 2026 GOLDENTECH SARL — Conakry
          </span>
          {/* Sur mobile, le bouton « Mettre à jour » prend la place du numéro de version. */}
          <span
            className={cn(
              "bg-primary-foreground/15 px-1.5 py-0.5 font-mono text-[10px] text-primary-foreground",
              updateAvailable && "hidden sm:inline",
            )}
          >
            v0.1.0
          </span>
        </div>
      </div>
    </footer>
  );
}
